/**
 * Estado de la sincronización. Compartido por las dos variantes.
 *
 * §9 dice que la sincronización es un detalle que el usuario no debe percibir, así que el estado
 * normal es discreto: una línea chica al lado de una nube. Discreto no es mudo, y esa es la
 * corrección que trajo este archivo: en el celular el indicador era sólo un ícono, así que un sync
 * que falló se veía idéntico a uno que subió todo. Lo que está en juego es si los datos existen
 * fuera del teléfono, y eso no se adivina de un ícono: se dice con palabras ("Al día", "3 por
 * subir", "Sin conexión", "No subió") en las dos variantes.
 */

import { useEffect, useRef, useState, type ReactNode } from 'react';
import { useLiveQuery } from 'dexie-react-hooks';

import { db } from '../datos/db.ts';
import { engancharSincronizacionAutomatica, sincronizar } from '../datos/sync.ts';
import { estaEnLinea } from '../datos/api.ts';
import { Aviso, Icono } from './componentes.tsx';
import { useRuta } from './router.tsx';

export function useEstadoSync() {
  const [enLinea, setEnLinea] = useState(estaEnLinea);
  const [sincronizando, setSincronizando] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const pendientes = useLiveQuery(() => db.sucios.count(), [], 0) ?? 0;
  const conError = useLiveQuery(() => db.sucios.filter((s) => s.ultimoError !== null).toArray(), [], []) ?? [];

  useEffect(() => {
    const alCambiar = () => setEnLinea(estaEnLinea());
    window.addEventListener('online', alCambiar);
    window.addEventListener('offline', alCambiar);
    const desenganchar = engancharSincronizacionAutomatica();
    return () => {
      window.removeEventListener('online', alCambiar);
      window.removeEventListener('offline', alCambiar);
      desenganchar();
    };
  }, []);

  // Empuje tras escritura, con espera. Los otros dos disparadores ('online' y volver al frente)
  // no ocurren nunca durante una sesión activa con la app al frente: un entrenamiento entero se
  // quedaba en el teléfono con el contador "N por subir" creciendo a la vista, que se lee como
  // "esto no anda". No es un intervalo (§9): corre sólo cuando la cola CAMBIÓ, un rato después
  // de la última escritura — el descanso entre series —, y no reintenta una cola que ya falló y
  // no se movió: para eso están el botón y los eventos.
  const ultimoIntento = useRef(-1);
  useEffect(() => {
    if (pendientes === 0 || !enLinea || pendientes === ultimoIntento.current) return;
    const timer = window.setTimeout(() => {
      ultimoIntento.current = pendientes;
      void sincronizar().catch(() => undefined);
    }, 20_000);
    return () => window.clearTimeout(timer);
  }, [pendientes, enLinea]);

  const sincronizarAhora = async (): Promise<void> => {
    setSincronizando(true);
    setError(null);
    try {
      await sincronizar();
    } catch (e) {
      setError(e instanceof Error ? e.message : 'No se pudo sincronizar.');
    } finally {
      setSincronizando(false);
    }
  };

  return { enLinea, sincronizando, pendientes, conError, error, sincronizarAhora };
}

export function IndicadorSync({ compacto = false }: { compacto?: boolean }): ReactNode {
  const { enLinea, sincronizando, pendientes, conError, error, sincronizarAhora } = useEstadoSync();
  const { ir } = useRuta();
  const [detalleAbierto, setDetalleAbierto] = useState(false);

  // Dos orígenes distintos para lo mismo: el intento manual que se cayó (sin red, sesión vencida)
  // y las filas que el servidor rechazó y siguen en la cola. Para el usuario significan una sola
  // cosa —eso no está arriba— así que se muestran igual.
  const fallo = error ?? conError[0]?.ultimoError ?? null;

  // Un sync que falla mientras el usuario mira tiene que decirlo solo. Esperar a que lo toque
  // sería dejar que fallar y andar se vean igual, que es justo lo que no puede pasar.
  useEffect(() => {
    if (error !== null) setDetalleAbierto(true);
  }, [error]);

  // Sin conexión, la cuenta de pendientes se dice sólo en la variante ancha: en la cabecera del
  // celular hay 358 px y ahí también viven el título de la pantalla, la ayuda y los ajustes. En
  // compacto esa cuenta va en el `title` y en el panel, que es donde no le roba lugar a nada.
  const etiqueta = !enLinea
    ? `Sin conexión${!compacto && pendientes > 0 ? ` · ${pendientes} por subir` : ''}`
    : fallo !== null
      ? 'No subió'
      : sincronizando
        ? 'Subiendo…'
        : pendientes > 0
          ? `${pendientes} por subir`
          : 'Al día';

  const explicacion =
    fallo !== null
      ? `No se pudo subir: ${fallo}`
      : !enLinea
        ? `Sin conexión. Lo que registres queda guardado en el teléfono y sube solo cuando vuelva la señal.${
            pendientes > 0 ? ` Hay ${pendientes} sin subir.` : ''
          }`
        : 'Sincronizar con la planilla';

  return (
    <div className="sync">
      <button
        type="button"
        className={`boton boton--chico boton--fantasma${fallo !== null ? ' sync--mal' : ''}`}
        onClick={() => {
          if (fallo !== null) setDetalleAbierto((v) => !v);
          else void sincronizarAhora();
        }}
        disabled={sincronizando}
        title={explicacion}
        aria-label={`Sincronización: ${etiqueta}`}
        {...(fallo !== null ? { 'aria-expanded': detalleAbierto } : {})}
        style={{ gap: 6, minHeight: 38 }}
      >
        <Icono nombre={fallo !== null ? 'alerta' : enLinea ? 'nube' : 'nube-tachada'} tamano={16} />
        <span className="nota--fina">{etiqueta}</span>
      </button>

      {detalleAbierto && fallo !== null && (
        <div className="sync-panel">
          <Aviso tono="importante">
            <div className="fila fila--entre">
              <strong>No se pudo subir</strong>
              <button
                type="button"
                className="boton boton--chico boton--fantasma"
                onClick={() => setDetalleAbierto(false)}
                aria-label="Cerrar"
              >
                <Icono nombre="cerrar" tamano={14} />
              </button>
            </div>
            <p className="nota" style={{ marginTop: 2 }}>
              {fallo}
            </p>
            <p className="nota--fina" style={{ marginTop: 4 }}>
              Lo que registraste sigue guardado en el teléfono: no se perdió nada.
            </p>
            <div className="fila" style={{ gap: 'var(--e2)', marginTop: 'var(--e3)', flexWrap: 'wrap' }}>
              <button
                type="button"
                className="boton boton--chico"
                onClick={() => void sincronizarAhora()}
                disabled={sincronizando}
              >
                {sincronizando ? 'Subiendo…' : 'Reintentar'}
              </button>
              <button
                type="button"
                className="boton boton--chico boton--fantasma"
                onClick={() => {
                  setDetalleAbierto(false);
                  ir('ajustes');
                }}
              >
                Ver en Ajustes
              </button>
            </div>
          </Aviso>
        </div>
      )}
    </div>
  );
}
